import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { toast } from 'react-toastify';
import EventBus from "../common/EventBus";
import { Modal, Button } from 'react-bootstrap';
import clientNetworkService from "../services/clientNetwork.service";

const ClientNetworkManager = ({ clientId }) => {
    const { user: currentUser } = useSelector((state) => state.auth);

    const [network, setNetwork] = useState("");
    const [originalNetwork, setOriginalNetwork] = useState("");
    const [hasConfig, setHasConfig] = useState(false);
    const [timestamps, setTimestamps] = useState({ created: null, updated: null });

    const [loading, setLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    useEffect(() => {
        if (!currentUser || !currentUser.roles.includes("ROLE_ADMIN")) {
            return;
        }
        if (clientId) {
            fetchClientNetworkConfig(clientId);
        } else {
            // Reset if clientId drops
            setNetwork("");
            setOriginalNetwork("");
            setHasConfig(false);
            setTimestamps({ created: null, updated: null });
        }
    }, [currentUser, clientId]);

    const fetchClientNetworkConfig = (clientId) => {
        setLoading(true);
        setNetwork("");
        setOriginalNetwork("");
        setHasConfig(false);
        setTimestamps({ created: null, updated: null });

        clientNetworkService.getClientNetwork(clientId).then(
            (response) => {
                if (response.data && response.data.network) {
                    setHasConfig(true);
                    setNetwork(response.data.network);
                    setOriginalNetwork(response.data.network);
                    setTimestamps({
                        created: response.data.createdAt,
                        updated: response.data.updatedAt
                    });
                }
                setLoading(false);
            },
            (error) => {
                setLoading(false);
            }
        );
    };

    const handleNetworkChange = (e) => {
        setNetwork(e.target.value);
    };

    const saveConfig = () => {
        if (!clientId) return;

        const cleanNetwork = network.trim();
        if (cleanNetwork === "") {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "IP Address or Network cannot be empty." });
            return;
        }

        const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        if (!ipv4Regex.test(cleanNetwork)) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Please enter a valid IPv4 address (e.g. 192.168.1.1)." });
            return;
        }

        const payload = {
            clientId: clientId,
            network: cleanNetwork
        };

        setLoading(true);
        clientNetworkService.saveClientNetwork(clientId, payload).then(
            (response) => {
                toast.success("Network configuration saved successfully.");
                fetchClientNetworkConfig(clientId);
            },
            (error) => {
                const _content = (error.response && error.response.data) || error.message;
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to save network configuration: " + _content });
                setLoading(false);
            }
        );
    };

    const deleteConfig = () => {
        if (!clientId || !hasConfig) return;
        setShowDeleteModal(true);
    };

    const confirmDeleteConfig = () => {
        setLoading(true);
        setShowDeleteModal(false);
        clientNetworkService.deleteClientNetwork(clientId).then(
            (response) => {
                toast.success("Network configuration deleted.");
                fetchClientNetworkConfig(clientId); 
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to delete network configuration." });
                setLoading(false);
            }
        );
    };

    if (!currentUser) return <Navigate to="/login" />;
    if (!currentUser.roles.includes("ROLE_ADMIN")) {
        return null;
    }

    return (
        <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Public IP Config</h5>
                {loading && <span className="spinner-border spinner-border-sm text-primary" role="status"></span>}
            </div>

            {clientId && (
                <div className="card shadow-sm border-secondary">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                        <span className="text-muted small">
                        </span>
                        {hasConfig && (
                            <span className="badge bg-secondary">
                                Last Updated: {timestamps.updated ? new Date(timestamps.updated).toLocaleString() : 'N/A'}
                            </span>
                        )}
                    </div>
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-12">
                                <label className="form-label fw-bold">Public IP Address</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={network}
                                    onChange={handleNetworkChange}
                                    placeholder="e.g. 192.168.1.1"
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        <hr className="mt-4 mb-4" />

                        <div className="d-flex justify-content-end gap-2">
                            {hasConfig && (
                                <button
                                    className="btn btn-danger"
                                    onClick={deleteConfig}
                                    disabled={loading}
                                >
                                    Delete Configuration
                                </button>
                            )}
                            <button
                                className="btn btn-success px-4"
                                onClick={saveConfig}
                                disabled={loading || network.trim() === ""}
                            >
                                {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                                Save Configuration
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to completely delete this network configuration?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={confirmDeleteConfig} disabled={loading}>
                        {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ClientNetworkManager;
